import axios from 'axios'
import convertUser from '@/api/converters/user'
import sortAlphabetically from '@/functions/sortAlphabetically'

/**
 * Get list of coaches
 */
function getList () {
  return axios
    .get('coaches/')
    .then(response => response.data
      .map(convertUser)
      .sort((a, b) => sortAlphabetically(a.profile.name, b.profile.name))
    )
}

/**
 * Get coach details
 * @param {Number} id Coach ID
 */
function getSingle (id) {
  return axios
    .get(`coaches/${id}/`)
    .then(response => convertUser(response.data))
}

/**
 * Get coach image
 * @param {Number} id Coach ID
 */
function getImage (id) {
  return axios
    .get(`coaches/${id}/hole-lichtbild/`, { responseType: 'blob' })
}

/**
 * Get coach training document
 * @param {Number} id Coach ID
 */
function getTraining (id) {
  return axios
    .get(`coaches/${id}/get-training-docs/`, { responseType: 'blob' })
}

/**
 * Get own coach training document
 */
function getOwnTraining () {
  return axios
    .get('own-training/', { responseType: 'blob' })
}

/**
 * Get coach certificate document
 * @param {Number} id Coach ID
 */
function getCertificate (id) {
  return axios
    .get(`coaches/${id}/get-certificates/`, { responseType: 'blob' })
}

/**
 * Get own coach certificate document
 */
function getOwnCertificate () {
  return axios
    .get('own-certificates/', { responseType: 'blob' })
}

/**
 * Approve coach by admin
 * @param {Number} id Coach ID
 */
function approve (id) {
  return axios
    .get(`coaches/${id}/approve/`)
}

/**
 * Update coach profile
 * @param {Number} id Profile ID
 * @param {Object} data Form data
 * @param {String} data.email Email address
 * @param {Boolean} data.membershipsBvc Member of _Bundesverband Coaching_
 * @param {Boolean} data.membershipsBvs Member of _Bundesverband Supervision_
 * @param {Boolean} data.membershipsOther Member of another certified coaching thing
 * @param {File} [data.documentsCertificate] Certificate PDF
 * @param {File} [data.documentsTraining] Training PDF
 * @param {File} [data.image] Portrait image
 * @param {Number} data.compensation Compensation
 * @param {String} data.address Street and number
 * @param {String} data.city City
 * @param {String} data.coreArea Biographical type of text
 * @param {String} data.firstName First name
 * @param {String} data.lastName Last name
 * @param {String} data.phone Phone number
 * @param {String} data.mobile Mobile phone number
 * @param {String} data.postalCode Postal code
 * @param {String} data.website URL of website
 */
function update (id, data) {
  const formData = new FormData()

  formData.append('first_name', data.firstName)
  formData.append('last_name', data.lastName)
  formData.append('homepage', data.website)
  formData.append('rate', data.compensation)
  formData.append('telephone', data.phone)
  formData.append('mobile', data.phoneMobile)
  formData.append('text', data.coreArea)
  formData.append('address', data.address)
  formData.append('zip_code', data.postalCode)
  formData.append('town', data.city)
  formData.append('qualification_bvc', data.membershipsBvc)
  formData.append('qualification_bvs', data.membershipsBvs)
  formData.append('qualification_other', data.membershipsOther)

  if (data.email) {
    formData.append('email', data.email)
  }

  if (data.image) {
    formData.append('lichtbild', data.image)
  }

  if (data.documentsTraining) {
    formData.append('training', data.documentsTraining)
  }

  if (data.documentsCertificate) {
    formData.append('certificates', data.documentsCertificate)
  }

  return axios
    .post(`coaches/${id}/edit/`, formData)
}

/**
 * Delete coach account
 * @param {Number} id Coach ID
 */
function deleteAccount (id) {
  return axios
    .delete(`coaches/${id}/`)
}

export default {
  getList,
  getSingle,
  getImage,
  getTraining,
  getOwnTraining,
  getCertificate,
  getOwnCertificate,
  approve,
  update,
  deleteAccount
}
